import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { FileTypeEnum, IFile } from '@repo/toolkit/types'
import Button from '@repo/ui/button'
import { PlusIcon } from '@repo/ui/icons'
import InformationBox from '@repo/ui/information-box'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { toast } from 'sonner'

import useTranslate from '../../../../lib/hooks/use-translate'
import UploadImageCard from '../../../elements/upload-image-card'

type FamilyImagesUploaderProps = {
  images: Partial<IFile>[]
  onChange: (images: Partial<IFile>[]) => void
  mode: 'primary' | 'secondary'
}

const FamilyImagesUploader = ({ images, onChange, mode }: FamilyImagesUploaderProps) => {
  const { t } = useTranslate()

  return (
    <InnerBox className="p-4 rounded-xl flex flex-col gap-2">
      <Typography
        variant="text"
        color={mode === 'primary' ? tailwindColors.black.DEFAULT : tailwindColors.primary.DEFAULT}
      >
        {t('affected.staff.questionnaire.host.family.pictures.title')}
      </Typography>

      <InformationBox isHiddenOnMobile={false}>
        <div className="flex flex-col gap-2">
          <Typography variant="caption" color={tailwindColors.black.DEFAULT}>
            {t('affected.staff.questionnaire.host.family.pictures.note.1')}
          </Typography>
          <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
            <strong className="text-black">
              {t('affected.staff.questionnaire.host.family.pictures.note.2')}
            </strong>
            {mode === 'primary'
              ? t('host.family.images.note')
              : t('affected.staff.questionnaire.host.family.pictures.note.3')}
          </Typography>
          <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
            <strong className="text-black">
              {t('affected.staff.questionnaire.host.family.pictures.note.nb')}
            </strong>{' '}
            {t('affected.staff.questionnaire.host.family.pictures.note.nb.text')}
          </Typography>
        </div>
      </InformationBox>

      <div className="grid grid-cols-3 gap-3 mobile:grid-cols-1">
        {images?.map((image, index) => (
          <UploadImageCard
            key={`image-${index}-caption`}
            index={index}
            image={image}
            onTextChange={(text) => {
              const updated = [...images]
              updated[index] = { ...updated[index], caption: text }
              onChange(updated)
            }}
            onImageUpload={(files) => {
              const file = files[0]
              if (file.size >= 2 * 1024 * 1024) {
                toast.error(t('images.size.error'))
                return
              }
              if (!file) return
              const updated = [...images]
              updated[index] = {
                ...file,
                type: FileTypeEnum.IMAGE,
                caption: updated[index]?.caption
              }
              onChange(updated)
            }}
            onImageDelete={() => {
              //remove the image but keep the caption
              const updated = [...images]
              updated[index] = {
                caption: updated[index]?.caption,
                type: FileTypeEnum.IMAGE
              }
              onChange(updated)
            }}
            onCardDelete={() => {
              const updated = [...images]
              updated.splice(index, 1)
              onChange(updated)
            }}
          />
        ))}
      </div>

      <Button
        label={t('affected.staff.questionnaire.host.family.pictures.upload.label.btn')}
        icon={PlusIcon}
        type="button"
        variant="ghost"
        className="w-full"
        onClick={() => {
          const updated = [
            ...images,
            { path: '', name: '', size: 0, type: FileTypeEnum.IMAGE, caption: '' }
          ]
          onChange(updated)
        }}
      />
    </InnerBox>
  )
}

export default FamilyImagesUploader
